import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, FileText, Target, Calendar, Youtube, Check, Loader2 } from "lucide-react";
import logo from '../../assets/images/envelope-artie.png';
import podcastIcon from '../../assets/images/podcast.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { apiClient } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { renderAsync } from 'docx-preview';
import { useRef } from "react";

export default function OnboardingV2Page4() {
    const navigate = useNavigate();
    const { data, updateData } = useOnboarding();
    const { user } = useAuthStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const docxContainerRef = useRef<HTMLDivElement>(null);

    // Manage local preview URL
    useEffect(() => {
        if (showPreview) {
            if (data.scriptFile) {
                const isDocx = data.scriptFile.name.toLowerCase().endsWith('.docx');
                
                if (isDocx) {
                    // For DOCX, we don't need a URL, we'll render the blob directly
                    // But we wait for the dialog to open and ref to be available
                    setTimeout(() => {
                        if (docxContainerRef.current && data.scriptFile) {
                            docxContainerRef.current.innerHTML = ''; // Clear previous content
                            renderAsync(data.scriptFile, docxContainerRef.current)
                                .then(() => console.log("DOCX rendered successfully"))
                                .catch(err => console.error("Error rendering DOCX:", err));
                        }
                    }, 100); // Small delay to ensure Dialog is rendered
                } else {
                    // For PDF, use Object URL
                    const url = URL.createObjectURL(data.scriptFile);
                    setPreviewUrl(url);
                    return () => {
                        URL.revokeObjectURL(url);
                        setPreviewUrl(null);
                    };
                }
            } else if (data.scriptId) {
                // If we don't have the file but have the ID, we can't easily preview DOCX locally without fetching it
                // For PDF, we can use the backend view URL (handled in the render)
            }
        }
    }, [showPreview, data.scriptFile, data.scriptId]);

    useEffect(() => {
        if (!data.scriptFile && !data.scriptId) {
            navigate('/onboarding-v2');
            return;
        }

        // Basic validation to ensure user didn't skip steps
        if (!data.projectTitle || !data.projectType || !data.budget) {
            toast.error("Please complete all project details before proceeding");
            // Optional: redirect to page 2?
            // navigate('/onboarding-v2/page2');
        }
    }, [data, navigate]);


    const getProjectTypeDisplay = () => {
        if (!data.projectType) return "Project Type";

        if ((data.projectType === "Vertical short" || data.projectType === "Long-form episodic") && data.episodeCount) {
            return `${data.projectType} (${data.episodeCount} episode${data.episodeCount !== 1 ? 's' : ''})`;
        }

        return data.projectType;
    };

    const handleNext = async () => {
        if (!data.projectTitle || !data.projectType || !data.budget) {
            toast.error("Please complete all project details before proceeding");
            return;
        }

        // Check if file was lost due to refresh (we have name but no file object) AND we haven't uploaded it yet
        if (data.scriptFileName && !data.scriptFile && !data.scriptId) {
            toast.error("Script file missing. Please re-upload your script.");
            navigate('/onboarding-v2'); // Go back to step 1
            return;
        }

        try {
            setIsSubmitting(true);

            // 1. Create the project
            const projectPayload = {
                title: data.projectTitle,
                status: "draft",
                brand_revenue_target: parseBudget(data.budget, data.projectType),
                target_30d_reach: 0,
                launch_date: new Date().toISOString().split('T')[0],
                created_via_onboarding: true,
                format: formatProjectType(data.projectType, data.episodeCount),
                episode_count: data.episodeCount,
                logline: data.logline,
                // Add other fields if necessary
            };

            const createdProject = await apiClient.createProject(projectPayload);
             // @ts-ignore
            const projectId = createdProject.id || createdProject._id;
            
            if (!projectId) throw new Error("Failed to create project");

            // 2. Associate script with the new project
            const scriptId = data.scriptId;
            
            if (!scriptId) {
                // Should not happen if flow is correct, but safe check
                throw new Error("Script ID missing. Please restart onboarding.");
            }

            try {
                await apiClient.updateScript(scriptId, { project_id: projectId }, true);
            } catch (e) {
                console.warn("Failed to update script project ID:", e);
                // Continue anyway, maybe it was already associated or handled differently
            }

            // 3. Update context with real IDs
            updateData({
                projectId: projectId,
                scriptId: scriptId
            });

            // 4. Navigate to loading/success page
            navigate('/onboarding-v2/loading');

        } catch (error) {
            console.error("Submission error:", error);
            toast.error("Failed to submit project. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper functions copied/adapted from Page 2
    const parseBudget = (str: string, projectType: string = "") => {
         if (!str || str === "I'm not sure") {
             switch (projectType) {
                 case "Vertical short": return 35000;
                 case "Long-form episodic": return 175000;
                 case "Short film": return 175000;
                 default: return 500000;
             }
         }
 
         const matches = str.match(/(\d+(?:\.\d+)?)\s*[kKmM]?/g);
         if (!matches) return 0;
 
         let maxVal = 0;
         matches.forEach(m => {
             const num = parseFloat(m);
             if (isNaN(num)) return;
             let multiplier = 1;
             const lower = m.toLowerCase();
             if (lower.includes('k')) multiplier = 1000;
             if (lower.includes('m')) multiplier = 1000000;
             const val = num * multiplier;
             if (val > maxVal) maxVal = val;
         });
         return maxVal;
     };

     const formatProjectType = (type: string, episodes: number) => {
        if (type === "Long-form episodic" || type === "Vertical short") {
            return `${type} (${episodes} episode${episodes !== 1 ? 's' : ''})`;
        }
        return type;
     };

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">


            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            All set. Here’s what <br /> I’ll review.
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2 max-w-[700px] mx-auto">
                            This helps me give you a thoughtful early read. You can edit anything before I start.
                        </p>
                    </div>

                    <div className="w-full space-y-6 animate-in slide-in-from-bottom-4 duration-500 delay-100">
                        {/* Summary Card */}
                        <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-0 rounded-[12px]">
                            <div className="p-8 pb-6">
                                <div className="flex flex-col md:flex-row gap-8">
                                    {/* Project Section - Left Column */}
                                    <div className="flex-1 space-y-4">
                                        <div>
                                            <h3 className="text-sm font-bold text-[oklch(0.55_0_0)] uppercase tracking-wider mb-1">Project</h3>
                                            <h2 className="text-2xl font-bold text-[oklch(0.2686_0_0)]">{data.projectTitle || "Untitled Project"}</h2>
                                            <p className="text-[oklch(0.55_0_0)]">{getProjectTypeDisplay()}</p>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-[oklch(0.96_0_0)]">
                                            <div className="p-2 bg-white rounded-md border border-[oklch(0.92_0_0)]">
                                                <FileText className="w-5 h-5 text-[oklch(0.55_0_0)]" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-[oklch(0.2686_0_0)]">
                                                    {data.scriptFileName || "No script uploaded"}
                                                </p>
                                                {/* Page count not available until processed */}
                                                <p className="text-xs text-[oklch(0.55_0_0)]">Ready to process</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Script Preview - Right Column */}
                                    <div
                                        className={`w-full md:w-32 bg-gray-50 rounded-lg border border-[oklch(0.96_0_0)] flex items-center justify-center min-h-[140px] ${(data.scriptFile || data.scriptId) ? 'cursor-pointer hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                                        onClick={() => (data.scriptFile || data.scriptId) && setShowPreview(true)}
                                        title={(data.scriptFile || data.scriptId) ? "Preview script" : "No script uploaded"}
                                    >
                                        <div className="text-center">
                                            <FileText className={`w-8 h-8 ${(data.scriptFile || data.scriptId) ? 'text-[oklch(0.2686_0_0)]' : 'text-[oklch(0.85_0_0)]'} mx-auto mb-2`} />
                                            <span className="text-xs text-[oklch(0.55_0_0)] font-medium">
                                                {(data.scriptFile || data.scriptId) ? "Preview" : "No Script"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="h-px bg-[oklch(0.92_0_0)] mb-8 mx-8"></div>

                            <div className="p-8 pt-2">
                                {/* Audience Section */}
                                <div className="mb-8">
                                    <h3 className="text-sm font-bold text-[oklch(0.2686_0_0)] mb-4">Audience</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Column 1: Social Connected */}
                                        <div className="space-y-3">
                                            {user?.youtube_data && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <Youtube className="w-4 h-4 shrink-0 text-red-600" />
                                                    <span className="text-sm truncate max-w-[120px]">Connected</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {data.instagram && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#E1306C" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{data.instagram}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {data.tiktok && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0 text-black" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{data.tiktok}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {data.spotify && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#1DB954" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.38 9.841-.719 13.44 1.56.42.24.6.841.301 1.261zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{data.spotify}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {data.applePodcast && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <img
                                                        src={podcastIcon}
                                                        alt="Apple Podcast"
                                                        className="w-4 h-4 shrink-0 object-contain"
                                                    />
                                                    <span className="text-sm truncate max-w-[120px]">{data.applePodcast}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {data.isEarly && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <span className="text-sm italic">Early stage / No social following</span>
                                                </div>
                                            )}
                                            {!user?.youtube_data && !data.instagram && !data.tiktok && !data.spotify && !data.applePodcast && !data.isEarly && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <span className="text-sm italic">No socials connected</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Column 2: IMDb Connected */}
                                        <div className="space-y-3">
                                            {user?.social_profiles?.find(p => p.platform === "imdb") ? (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <img
                                                        src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg"
                                                        alt="IMDb"
                                                        className="w-11 h-auto object-contain"
                                                    />
                                                    <span className="text-sm">Connected</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <div className="w-11 h-8 flex items-center justify-center">
                                                        <img
                                                            src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg"
                                                            alt="IMDb"
                                                            className="w-full h-auto"
                                                        />
                                                    </div>
                                                    <span className="text-sm italic">Not connected</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px bg-[oklch(0.92_0_0)] mb-8"></div>

                                {/* Brand Support Goal Section */}
                                <div>
                                    <h3 className="text-sm font-bold text-[oklch(0.2686_0_0)] mb-4">Project Budget</h3>
                                    <div className="flex items-center gap-4">
                                        <Target className="w-8 h-8 text-[oklch(0.55_0_0)] stroke-1" />
                                        <div>
                                            <div className="text-2xl font-bold text-[oklch(0.2686_0_0)]">{data.budget || "$0"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Timeline Card */}
                        <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-8 rounded-[12px]">
                            <h3 className="text-lg font-bold text-[oklch(0.2686_0_0)] mb-6">What happens next</h3>

                            <div className="space-y-8 relative">
                                {/* Vertical Line */}
                                {/* <div className="absolute left-[15px] top-2 bottom-4 w-0.5 bg-[oklch(0.96_0_0)] -z-10"></div> */}

                                {/* Step 1 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">1</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Initial assessment begins immediately</span>
                                            <span className="px-2 py-0.5 rounded-md bg-[oklch(0.96_0_0)] text-[oklch(0.2686_0_0)] text-xs font-medium border border-[oklch(0.92_0_0)]">1-2 minutes</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            I'll analyze your script and audience signals to estimate what your project could earn from brand partnerships. You'll see initial insights within minutes.
                                        </p>
                                    </div>
                                </div>

                                {/* Step 2 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">2</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Professional review follows</span>
                                            <span className="px-2 py-0.5 rounded-md bg-[oklch(0.96_0_0)] text-[oklch(0.2686_0_0)] text-xs font-medium border border-[oklch(0.92_0_0)]">3-5 business days</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            Our AI works with WGA writers and brand strategists to assess craft and market fit - giving you insights to sharpen your commercial positioning. This part takes a little longer, but it's worth it.
                                        </p>
                                    </div>
                                </div>

                                {/* Step 3 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">3</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Packaging and next steps</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            I'll pull everything together into actionable recommendations to best position your project for commercial success. Expect to spend a few hours over 1-2 weeks on things like documenting your production plan, growing your audience, and assembling what brands need to say yes.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Callout Box */}
                            <div className="mt-8 bg-[oklch(0.98_0_0)] rounded-xl p-4 flex items-start gap-4 border border-[oklch(0.92_0_0)]">
                                <div className="w-10 h-10 rounded-full bg-white border border-[oklch(0.92_0_0)] flex items-center justify-center shrink-0 shadow-sm">
                                    <Calendar className="w-5 h-5 text-[oklch(0.2686_0_0)]" />
                                </div>
                                <div>
                                    <p className="font-bold text-[oklch(0.2686_0_0)] text-sm mb-1">First matching window closes soon</p>
                                    <p className="text-xs text-[oklch(0.55_0_0)] leading-normal">Complete your packaging steps by then to be eligible for our first round of brand matching.</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={() => navigate('/onboarding-v2/page3-2', { state: { keepData: true } })}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSubmitting}
                            >
                                Get an early read
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>

            </main>

            {/* Script Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col gap-0 outline-none">
                    <div className="px-6 py-4 border-b bg-white shrink-0">
                        <h3 className="text-lg font-semibold">{data.scriptFileName || 'Script Preview'}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto bg-gray-100 p-4 min-h-0">
                        {/* If we have a local file and it's DOCX */}
                        {data.scriptFile?.name.toLowerCase().endsWith('.docx') ? (
                            <div ref={docxContainerRef} className="bg-white shadow-lg min-h-full p-8 mb-8" />
                        ) : (
                            /* If it's not local DOCX, we try to use previewUrl (local PDF) or backend view URL (PDF) */
                            /* Note: Backend view for DOCX is not implemented here, assuming PDF for backend view */
                            (previewUrl || data.scriptId) ? (
                                <iframe
                                    src={previewUrl || `${import.meta.env.VITE_API_BASE_URL}/api/v1/scripts/${data.scriptId}/view#toolbar=0&navpanes=0&scrollbar=1`}
                                    className="w-full h-full border-0"
                                    title="Script Preview"
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                    Preview not available
                                </div>
                            )
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}